import { Modal, Button, Progress } from "@mantine/core";
import {
	createProductWithQueue,
	updateProductWithQueue,
	getProductsChannels,
	getAllProductsChannelsTypesLinks,
} from "../redux/slices/product";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { formateMetafield, getMetafields } from "../redux/slices/metafields";
import { useUser } from "src/context/UserContext";
import type { NewTagProperties } from "src/@types/props";
import { checkToken } from "src/auth/utils";

export default function ConfirmModal({
	opened,
	actions,
	rowData,
	setAwaitingProduct,
	handleClosePreview,
	ignoreUpdate,
	ignoreCreate,
	dbData,
}: NewTagProperties) {
	const { user } = useUser();

	const { t } = useTranslation();

	const [importStatus, setImportStatus] = useState<boolean>(false);
	const [passDelayControl, setPassDelayControl] = useState<boolean>(false);
	const [progress, setProgress] = useState<number>(0);
	const [total, setTotal] = useState<number>(0);

	const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setPassDelayControl(event.target.checked);
	};

	const handleImportClick = async () => {
		setImportStatus(true);
		checkToken();
		const { data } = await getMetafields();
		const products = formateMetafield(rowData, data);
		const toCreate = ignoreCreate
			? null
			: products?.filter(
					(product: { Action: string }) =>
						product.Action === t("confirmModal.action.create"),
				);
		const toUpdate = ignoreUpdate
			? null
			: products?.filter(
					(product: { Action: string }) =>
						product.Action === t("confirmModal.action.update"),
				);
		let isError = false;
		try {
			setImportStatus(true);
			const totalProducts = (toCreate?.length || 0) + (toUpdate?.length || 0);
			setTotal(totalProducts);
			let processed = 0;
			if (toCreate?.length && user) {
				const productChannels = await (async () => {
					const links: {
						id: string;
						publicationExternalId: string;
						publicationTypeId: string;
					}[] = await getAllProductsChannelsTypesLinks();

					const names: {
						publicationId: string;
						publicationName: string;
					}[] = await getProductsChannels();

					const namesMap = names.reduce(
						(acc, { publicationId, publicationName }) => {
							acc[publicationId] = publicationName;
							return acc;
						},
						{} as { [key: string]: string },
					);

					const DEFAULT_PUBLICATIONS = [
						"Online Store",
						"Point of Sale",
						"Inbox",
						"Hydrogen",
						"Headless",
						"Google & YouTube",
					];

					return links.reduce(
						(acc, { publicationExternalId, publicationTypeId }) => {
							const name = namesMap[publicationExternalId] || "Unknown";
							if (DEFAULT_PUBLICATIONS.includes(name)) {
								acc.push(publicationTypeId);
							}
							return acc;
						},
						[] as string[],
					);
				})();
				// Découpage en lots pour la progression
				const BATCH_SIZE = 50;
				for (let i = 0; i < toCreate.length; i += BATCH_SIZE) {
					const batch = toCreate.slice(i, i + BATCH_SIZE);
					await createProductWithQueue(
						batch,
						user.id,
						productChannels,
						passDelayControl,
					);
					processed += batch.length;
					setProgress(processed);
				}
			}

			if (toUpdate?.length && user) {
				const BATCH_SIZE = 50;
				for (let i = 0; i < toUpdate.length; i += BATCH_SIZE) {
					const batch = toUpdate.slice(i, i + BATCH_SIZE);
					await updateProductWithQueue(batch, user.id, dbData);
					processed += batch.length;
					setProgress(processed);
				}
			}

			notifications.show({
				title: "Success",
				message: `${t("confirmModal.notification.success")}`,
				color: "blue",
			});
			setAwaitingProduct(true);
			handleClosePreview();
			actions.close();
		} catch (error) {
			isError = true;
			notifications.show({
				title: "Error",
				message: `${t("confirmModal.notification.error")},${error.message}`,
				color: "red",
			});
		} finally {
			setImportStatus(false);
			setProgress(0);
			setTotal(0);
			if (!isError) {
				window.location.reload();
			}
		}
	};

	return (
		<Modal
			zIndex={300}
			opened={opened}
			onClose={() => (importStatus ? undefined : actions.close())}
			title={t("confirmModal.title")}
			centered
			closeOnClickOutside={!importStatus}
			withCloseButton={!importStatus}
		>
			{user?.role === "ADMIN" && (
				<div>
					<label htmlFor="delay-control-checkbox">
						<input
							id="delay-control-checkbox"
							type="checkbox"
							checked={passDelayControl}
							onChange={handleCheckboxChange}
						/>
						{t("confirmModal.label")}
					</label>
				</div>
			)}
			<Button
				disabled={importStatus}
				fullWidth
				onClick={() => handleImportClick()}
			>
				{t("confirmModal.import")}
			</Button>
			{importStatus && (
				<Progress
					style={{ marginTop: 10 }}
					value={total > 0 ? Math.round((progress / total) * 100) : 0}
					children={`${progress} / ${total}`}
					animated
				/>
			)}
		</Modal>
	);
}
